/**
 * En qué puertos de la placa entra cada módulo.
 *
 * Es lo que dicen los stickers de colores de los puertos: cada color es una
 * categoría de pines de educablocks-boards, y un puerto que acepta varias lleva
 * el fondo partido. Un módulo entra en un puerto si alguna de sus categorías está
 * entre las del puerto.
 *
 * Esto es del front y no de modules.py: allá se calibran las lecturas, y los
 * nombres de módulo (MODULES de moduleTable) son la clave con la que se cruza.
 */

/**
 * Las categorías de pines que pinta un sticker, en el orden en que se reparten el
 * fondo: rosa y azul arriba, el resto abajo.
 */
export const KIND = {
  ANALOG: 'analog',
  DIGITAL: 'digital',
  JOYSTICK: 'joystick',
  ULTRASONIC: 'ultrasonic',
  PWM: 'pwm',
  I2C: 'i2c',
  MOTOR: 'motorPwm',
};

export const KIND_ORDER = [KIND.ANALOG, KIND.DIGITAL, KIND.JOYSTICK, KIND.ULTRASONIC, KIND.PWM, KIND.I2C, KIND.MOTOR];

// Un módulo que entra en cualquier puerto.
export const ANY_KIND = 'any';

/**
 * Cada módulo del catálogo: la clave de su nombre en i18n
 * (blocks.blockly.modules.<id>) y las categorías en las que entra.
 *
 * Salen de la columna ColorFicha de la planilla de componentes: rosa analog, azul
 * digital, amarillo pwm, rojo ultrasonic (dos pines), gris joystick (tres pines),
 * violeta i2c. Una ficha de dos colores entra en cualquiera de los dos, y la
 * multicolor en cualquier puerto.
 *
 * `ports` restringe un módulo a ciertos puertos en las placas que los tienen: el
 * Servo AT solo va en SAT1 y SAT2, aunque el P2 de la Power también sea pwm. El
 * Motor DC no tiene color en la planilla: se asume amarillo.
 *
 * 'Servo' no es del catálogo: es lo que nombra moduleTable en los puertos de servo
 * de la Power, que se leen aparte.
 */
export const MODULE_INFO = {
  'Seguidor de linea': { id: 'lineFollower3', kinds: [KIND.JOYSTICK] },
  Conversor: { id: 'converter', kinds: [ANY_KIND] },
  Tactil: { id: 'touch', kinds: [KIND.DIGITAL] },
  'Motor DC 100RPM': { id: 'dcMotor', kinds: [KIND.PWM] },
  Obstaculo: { id: 'obstacle', kinds: [KIND.DIGITAL] },
  'Servo AT': { id: 'servoHighTorque', kinds: [KIND.PWM], ports: ['SAT1', 'SAT2'] },
  Color: { id: 'color', kinds: [KIND.I2C] },
  Joystick: { id: 'joystick', kinds: [KIND.JOYSTICK] },
  Potenciometro: { id: 'potentiometer', kinds: [KIND.ANALOG] },
  Luz: { id: 'light', kinds: [KIND.ANALOG] },
  Sonido: { id: 'sound', kinds: [KIND.ANALOG] },
  Boton: { id: 'button', kinds: [KIND.DIGITAL] },
  'Temperatura y humedad': { id: 'temperatureHumidity', kinds: [KIND.DIGITAL] },
  Gas: { id: 'gas', kinds: [KIND.ANALOG] },
  Optico: { id: 'lineFollower1', kinds: [KIND.DIGITAL] },
  'LEDs RGB': { id: 'ledStrip', kinds: [KIND.DIGITAL] },
  Zumbador: { id: 'buzzer', kinds: [KIND.DIGITAL] },
  Suelo: { id: 'soil', kinds: [KIND.ANALOG] },
  Semaforo: { id: 'trafficLight', kinds: [KIND.DIGITAL] },
  'LED blanco': { id: 'whiteLed', kinds: [KIND.DIGITAL, KIND.PWM] },
  'LED RGB': { id: 'rgbLed', kinds: [KIND.DIGITAL] },
  Ultrasonido: { id: 'ultrasonic', kinds: [KIND.ULTRASONIC] },
  'Matriz 8x8 LGO': { id: 'matrix8x8Lgo', kinds: [KIND.I2C] },
  'Matriz 8x8': { id: 'matrix8x8', kinds: [KIND.I2C] },
  Rele: { id: 'relay', kinds: [KIND.DIGITAL] },
  'Motor con helice': { id: 'fan', kinds: [KIND.ULTRASONIC] },
  'Servomotor MG90S': { id: 'servo', kinds: [KIND.DIGITAL, KIND.PWM] },
  'Matriz LED 16x8': { id: 'matrix16x8', kinds: [KIND.I2C] },
  Camara: { id: 'camera', kinds: [KIND.I2C] },
  'Pantalla LCD': { id: 'lcd', kinds: [KIND.I2C] },
  Servo: { id: 'servo', kinds: [KIND.PWM] },
};

const EDUPLUG_MINI_PORTS = ['0', '1', '2', 'IIC', 'MD', 'MI'];

/**
 * El puerto del protocolo (el byte de GTMD/EVMD) contra la etiqueta del pin en
 * educablocks-boards, que es la del sticker. Va por perfil de placa y no por
 * plataforma: el firmware AVR numera P0..P9 sin saber en qué placa está.
 *
 * Power: los nombres de PORT_NAMES de ModuleDetector.cpp. Quedan afuera P6 y P7
 * (MI y MD), que miden la corriente de los motores y no leen IDs: un motor andando
 * puede caer en un rango del catálogo y no es un módulo mal puesto. Mini (las
 * placas con el pinout CREABOTS): los canales del ADS1115 en el orden de sus
 * puertos.
 *
 * Bhoot, LGO 2.0 y Omni se suman cuando el firmware lea sus módulos.
 */
export const PORT_LABELS = {
  eduplugPower: ['P0', 'P1', 'P2', 'IIC', 'SAT1', 'SAT2'],
  creabotsInitiation: EDUPLUG_MINI_PORTS,
  creabotsAutomation: EDUPLUG_MINI_PORTS,
  creabotsMovement: EDUPLUG_MINI_PORTS,
  creabotsAi: EDUPLUG_MINI_PORTS,
  creabotsMovementAi: EDUPLUG_MINI_PORTS,
};

/**
 * La etiqueta del puerto `port` en la placa, o null si la placa no tiene mapeo o
 * el puerto no existe. `board` es la placa de la app ({ profile, pins }).
 */
export const portLabel = (board, port) => {
  const labels = PORT_LABELS[(board || {}).profile];
  return (labels && labels[port]) || null;
};

/**
 * Las categorías de un puerto, en el orden de KIND_ORDER: las de pins en las que
 * aparece su etiqueta.
 */
export const portKinds = (board, label) => {
  const pins = (board || {}).pins || {};
  return KIND_ORDER.filter((kind) => (pins[kind] || []).some(([pinLabel]) => pinLabel === label));
};

// El grupo de un puerto en la tira de stickers: los de módulos, los IIC, los de motor.
const portGroup = (kinds) => {
  if (kinds.includes(KIND.MOTOR)) return 2;
  return kinds.includes(KIND.I2C) ? 1 : 0;
};

/**
 * Los puertos de la placa en el orden de los stickers: primero los de módulos
 * por número, después los IIC y al final los de motor.
 */
export const boardPorts = (board) => {
  const pins = (board || {}).pins || {};
  const labels = [];
  KIND_ORDER.forEach((kind) => (pins[kind] || []).forEach(([label]) => {
    if (!labels.includes(label)) labels.push(label);
  }));
  const group = (label) => portGroup(portKinds(board, label));
  return labels.sort((a, b) => (group(a) - group(b)) || a.localeCompare(b, undefined, { numeric: true }));
};

/**
 * Si un módulo entra en el puerto `label` de la placa. Un módulo que no está en
 * la tabla no se juzga: entra.
 */
export const moduleFits = (board, name, label) => {
  const info = MODULE_INFO[name];
  if (!info) return true;
  if (info.ports && info.ports.some((port) => boardPorts(board).includes(port))) {
    return info.ports.includes(label);
  }
  if (info.kinds.includes(ANY_KIND)) return true;
  const kinds = portKinds(board, label);
  return info.kinds.some((kind) => kinds.includes(kind));
};

/**
 * Los puertos de la placa en los que entra el módulo, en el orden de los
 * stickers.
 */
export const portsForModule = (board, name) => boardPorts(board)
  .filter((label) => moduleFits(board, name, label));

export const MODULE_EVENT = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};

// Lo que identifica lo que hay en un puerto: vacío, un módulo, o algo sin nombre.
const contentKey = (reading) => {
  if (!reading || !reading.present) return '';
  return reading.names.join('/') || '?';
};

/**
 * Qué se enchufó y qué se desenchufó desde lo último que se avisó.
 *
 * `known` es lo último avisado de cada puerto ({ puerto: lectura }) y `readings`
 * lo que trae el snapshot del link. Devuelve lo nuevo que hay que recordar y los
 * eventos, en orden de puerto:
 *
 *   { type, port, label, reading }
 *
 * `reading` es la lectura del módulo del que se habla: la nueva al conectar, la
 * que se tenía al desconectar, porque el EVMD de desconexión llega en 0 y sin
 * nombre.
 *
 * Solo cuenta lo que se leyó: un puerto que no está en `readings` no se dio por
 * vacío. Así el snapshot vacío de un link que se reinicia para instalar el
 * firmware no desenchufa nada, y lo que vuelve a aparecer después del reinicio
 * es lo mismo que ya se sabía. Los puertos sin etiqueta (P6/P7 de la Power, las
 * placas sin mapeo) no se miran.
 */
export const moduleEvents = (board, known, readings) => {
  const next = { ...known };
  const events = [];
  readings.forEach((reading) => {
    const label = portLabel(board, reading.port);
    if (!label) return;
    const before = known[reading.port];
    next[reading.port] = reading;
    if (contentKey(before) === contentKey(reading)) return;
    if (before && before.present) {
      events.push({ type: MODULE_EVENT.DISCONNECTED, port: reading.port, label, reading: before });
    }
    if (reading.present) {
      events.push({ type: MODULE_EVENT.CONNECTED, port: reading.port, label, reading });
    }
  });
  return { known: next, events };
};

/**
 * El nombre con el que se habla de una lectura: la clave de i18n de su módulo, o
 * null si no se sabe cuál es (desconocido o ambiguo).
 */
export const moduleMessageId = (reading) => {
  if (!reading || reading.names.length !== 1) return null;
  const info = MODULE_INFO[reading.names[0]];
  return info ? `blocks.blockly.modules.${info.id}` : null;
};

/**
 * Si lo leído en un puerto está mal enchufado: una lectura de interpretModule en
 * la placa `board`. Es null si no hay nada que objetar, o lo que hace falta para
 * avisar:
 *
 *   { port, label, name, ports, reading }
 *
 * Una lectura ambigua solo se objeta si ninguno de sus candidatos entra, y se
 * nombra con el primero. Lo que no se pudo nombrar (vacío, desconocido) o un
 * puerto que la placa no tiene mapeado no se juzga.
 */
export const misplacedModule = (board, reading) => {
  const label = portLabel(board, reading.port);
  if (!label || reading.names.length === 0) return null;
  if (reading.names.some((name) => moduleFits(board, name, label))) return null;
  const [name] = reading.names;
  return {
    port: reading.port,
    label,
    name,
    ports: portsForModule(board, name),
    reading,
  };
};
